import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';

export default function LiquidCursor() {
  const bubbleRef = useRef(null);
  const filterTurbRef = useRef(null);
  const filterDispRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const currentColorRef = useRef('#ffffff');
  
  const bobaColors = ['#000000', '#3b82f6', '#ef4444', '#ec4899', '#10b981'];
  
  const getRandomBobaColor = () => {
    return bobaColors[Math.floor(Math.random() * bobaColors.length)];
  };

  const shouldHideCursor = useMemo(() => {
    if (typeof window === 'undefined') return false;
    
    // Windows-Laptops mit Touch erkennen und Cursor NICHT verstecken
    const isWindows = navigator.platform?.toLowerCase().includes('win') || 
                     navigator.userAgent?.toLowerCase().includes('windows');
    
    // Smartphone/Tablet-Erkennung: kleine Bildschirme + Touch
    const isSmallScreen = window.innerWidth <= 768 || window.innerHeight <= 768;
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const hasCoarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    
    // Cursor auf Windows-Laptops IMMER anzeigen, auch mit Touch
    if (isWindows) return false;
    
    // Verstecke nur bei kleinen Touch-Geräten (Smartphones/Tablets, aber nicht Windows)
    return hasTouch && (isSmallScreen || hasCoarsePointer);
  }, []);

  useEffect(() => {
    if (shouldHideCursor) return; // Cursor deaktiviert auf kleinen Touch-Geräten
    setEnabled(true);
    document.body.classList.add('custom-cursor');
    return () => {
      document.body.classList.remove('custom-cursor');
    };
  }, [shouldHideCursor]);

  useEffect(() => {
    if (!enabled || !bubbleRef.current) return;

    const bubble = bubbleRef.current;
    // Anfangs außerhalb, damit kein Sprung sichtbar
    gsap.set(bubble, { x: -100, y: -100, scale: 0.8 });

    // Smooth-Follow Setter
    const setX = gsap.quickTo(bubble, 'x', { duration: 0.25, ease: 'power3.out' });
    const setY = gsap.quickTo(bubble, 'y', { duration: 0.25, ease: 'power3.out' });

    const move = (e) => {
      const x = e.clientX - 18; // Hälfte von Größe (36px)
      const y = e.clientY - 18;
      setX(x);
      setY(y);
      // sanftes Pulsieren bei Bewegung
      gsap.to(bubble, { scale: 1, duration: 0.2, overwrite: 'auto' });
    };

    const down = () => {
      setIsClicked(true);
      const newColor = getRandomBobaColor();
      currentColorRef.current = newColor;
      
      // Morph zu Boba-Blob mit zufälliger Farbe (aber transparent wie Popping Boba)
      gsap.to(bubble, { 
        scale: 1.2, 
        duration: 0.3, 
        ease: 'elastic.out(1, 0.8)',
        onStart: () => {
          // Farbig aber glasartig-transparent (wie echte Popping Bobas)
          const colorWithAlpha = newColor + '20'; // 12% Opacity für mehr Transparenz
          const borderColorWithAlpha = newColor + '60'; // 37% Opacity für Border
          bubble.style.backgroundColor = colorWithAlpha;
          bubble.style.borderColor = borderColorWithAlpha;
          // Magnifikations-Effekt verstärken (ohne Blur)
          bubble.style.backdropFilter = 'saturate(150%) brightness(130%)';
        }
      });
      
      // Intensiviere den Liquid-Effekt beim Klick
      if (filterTurbRef.current && filterDispRef.current) {
        gsap.to(filterTurbRef.current, { 
          attr: { baseFrequency: '0.025 0.04' }, 
          duration: 0.3,
          ease: 'power2.out'
        });
        gsap.to(filterDispRef.current, { 
          attr: { scale: 15 }, 
          duration: 0.3,
          ease: 'power2.out'
        });
      }
    };
    
    const up = () => {
      setIsClicked(false);
      
      // Zurück zu normalem Glassmaus-Icon
      gsap.to(bubble, { 
        scale: 1, 
        duration: 0.4, 
        ease: 'elastic.out(1, 0.6)',
        onStart: () => {
          bubble.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          bubble.style.borderColor = 'rgba(255, 255, 255, 0.3)';
          // Magnifikations-Effekt zurück zu normal (ohne Blur)
          bubble.style.backdropFilter = 'saturate(130%) brightness(115%)';
        }
      });
      
      // Liquid-Effekt zurücksetzen
      if (filterTurbRef.current && filterDispRef.current) {
        gsap.to(filterTurbRef.current, { 
          attr: { baseFrequency: '0.008 0.015' }, 
          duration: 0.5,
          ease: 'power2.out'
        });
        gsap.to(filterDispRef.current, { 
          attr: { scale: 6 }, 
          duration: 0.5,
          ease: 'power2.out'
        });
      }
    };

    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('pointerdown', down, { passive: true });
    window.addEventListener('pointerup', up, { passive: true });

    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerdown', down);
      window.removeEventListener('pointerup', up);
    };
  }, [enabled]);

  // Flüssigkeits-Animation (dezent) innerhalb des Filters
  useEffect(() => {
    if (!enabled || !filterTurbRef.current || !filterDispRef.current) return;
    const tl = gsap.timeline({ repeat: -1, yoyo: true, defaults: { ease: 'sine.inOut' } });
    tl.to(filterTurbRef.current, { attr: { baseFrequency: '0.012 0.02' }, duration: 3 })
      .to(filterDispRef.current, { attr: { scale: 10 }, duration: 3 }, 0);
    return () => tl.kill();
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div className="fixed inset-0 z-[60] pointer-events-none select-none">
      <svg width="0" height="0" className="absolute">
        <defs>
          <filter id="liquidCursorFilter">
            <feTurbulence ref={filterTurbRef} type="fractalNoise" baseFrequency="0.008 0.015" numOctaves="2" seed="3" />
            <feDisplacementMap ref={filterDispRef} in="SourceGraphic" scale="6" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
      </svg>

      <div
        ref={bubbleRef}
        className="absolute top-0 left-0 w-9 h-9 rounded-full border border-white/30 bg-white/10 shadow-sm"
        style={{ 
          filter: 'url(#liquidCursorFilter)',
          backdropFilter: 'saturate(130%) brightness(115%)',
          transform: 'scale(1)',
          transformOrigin: 'center'
        }}
      />
    </div>
  );
}


